import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

const inputCls = "w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50";

export const AuthModal = ({ open, onClose }) => {
  const { login, register, googleLogin } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form.username, form.email, form.password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-modal" className="max-w-sm border-white/10 bg-[#111114] text-white">
        <DialogTitle className="font-heading text-xl font-black tracking-tight">
          {tab === "login" ? "Welcome back" : "Join ZoundEngineer"}
        </DialogTitle>
        <DialogDescription className="text-sm text-white/50">
          {tab === "login" ? "Sign in to your producer account" : "Create your producer account"}
        </DialogDescription>

        <button
          data-testid="google-login-btn"
          onClick={googleLogin}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-cyan-500/40"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {tab === "register" && (
            <input data-testid="auth-username-input" value={form.username} onChange={set("username")}
                   placeholder="Username (3-20 chars)" className={inputCls} required />
          )}
          <input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")}
                 placeholder="Email" className={inputCls} required />
          <input data-testid="auth-password-input" type="password" value={form.password} onChange={set("password")}
                 placeholder="Password (min 8 chars)" className={inputCls} required />
          {error && <p data-testid="auth-error" className="text-xs text-red-400">{error}</p>}
          <button data-testid="auth-submit-btn" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-500 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {tab === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          data-testid="auth-switch-tab"
          onClick={() => { setTab(tab === "login" ? "register" : "login"); setError(""); }}
          className="text-center text-xs text-white/40 transition-colors duration-200 hover:text-white"
        >
          {tab === "login" ? "No account yet? Create one" : "Already have an account? Sign in"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
